import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import { generateUUID } from 'support/helpers/generic/generic';

export enum NotificationType {
  success = 'success',
  error = 'error',
}

export type Notification = {
  id: string;
  title: string;
  subtitle?: string;
  type: NotificationType;
};

export type State = {
  notifications: Array<Notification>;
};

export type Actions = {
  addNotification: (text: { title: string; subtitle?: string } | string, type?: NotificationType) => void;
  removeNotification: (id: string) => void;
};

/**
 * Bounds how many cards can be mounted and announced at once. The expanded stack scrolls, so this
 * is no longer about keeping the column on screen — only about a bulk action queueing up hundreds.
 */
export const MAX_NOTIFICATIONS = 10;

export const initialState: State = {
  notifications: [],
};

export const useNotificationStore = create(
  immer<State & Actions>((set) => ({
    ...initialState,

    addNotification: (
      text: Parameters<Actions['addNotification']>['0'],
      type: NotificationType = NotificationType.success,
    ) => {
      set((state) => {
        state.notifications.push({
          id: generateUUID(),
          title: typeof text === 'string' ? text : text.title,
          subtitle: typeof text === 'string' ? undefined : text.subtitle,
          type,
        });

        if (state.notifications.length > MAX_NOTIFICATIONS) {
          state.notifications.splice(0, state.notifications.length - MAX_NOTIFICATIONS);
        }
      });
    },

    removeNotification: (id: string) => {
      set((state) => {
        state.notifications = state.notifications.filter((notification) => notification.id !== id);
      });
    },
  })),
);

export const addNotification = (
  text: Parameters<Actions['addNotification']>['0'],
  type: NotificationType = NotificationType.success,
) => {
  return useNotificationStore.getState().addNotification(text, type);
};
export const removeNotification = (id: string) => {
  return useNotificationStore.getState().removeNotification(id);
};
